/**
 * "The person really left the app and came back" — on native.
 *
 * Capacitor's `appStateChange` reports `isActive` from iOS's
 * didBecomeActive / willResignActive, and iOS resigns-active for every
 * system sheet laid over the app: the Google sign-in alert, the StoreKit
 * purchase sheet, a permission prompt, Control Centre. So "isActive: true"
 * also fires when one of those sheets CLOSES — and the Capgo apply-on-
 * foreground reloaded the WebView right there, mid sign-in or mid purchase
 * (review 2026-10-17).
 *
 * `pause` / `resume` come from didEnterBackground / willEnterForeground,
 * which sheets don't trigger. On top of that, a resume only counts after a
 * pause this gate saw itself (pause-first), so a stray resume — one
 * delivered at launch, or twice — never acts.
 */

export interface PauseFirstGate {
  /** The app went to the background. */
  pause(now?: number): void
  /** The app came back. Returns how long it was away (ms), or null when no
   *  pause was seen since the last resume — then the caller must not act. */
  resume(now?: number): number | null
}

export function createPauseFirstGate(): PauseFirstGate {
  let pausedAt: number | null = null
  return {
    pause(now = Date.now()) {
      // Keep the FIRST pause: a second one (both events delivered) must not
      // shorten the measured time away.
      if (pausedAt === null) pausedAt = now
    },
    resume(now = Date.now()) {
      if (pausedAt === null) return null
      const away = Math.max(0, now - pausedAt)
      pausedAt = null
      return away
    },
  }
}

/**
 * Listen for genuine background → foreground returns on native. `onReturn`
 * gets the time away in ms. Resolves to a remover. Never rejects: a shell
 * without the App plugin just never calls back.
 */
export async function onGenuineForeground(
  onReturn: (awayMs: number) => void,
  onLeave?: () => void,
): Promise<() => void> {
  try {
    const { App: CapApp } = await import('@capacitor/app')
    const gate = createPauseFirstGate()
    const handles = await Promise.all([
      CapApp.addListener('pause', () => {
        gate.pause()
        onLeave?.()
      }),
      CapApp.addListener('resume', () => {
        const away = gate.resume()
        if (away !== null) onReturn(away)
      }),
    ])
    return () => { for (const h of handles) h.remove().catch(() => {}) }
  } catch {
    return () => {}
  }
}
